import type { PropType } from "vue";

import { useTextareaAutosize } from "@vueuse/core";
import { computed, reactive, toRef, watchEffect } from "vue";

import type {
  UseInputWrapperAtomProps,
  UseInputWrapperAtomScoped,
} from "@/lib/components/logic/atoms/input/useInputWrapperAtom";
import type { Size } from "@/lib/components/types";

import { size } from "@/lib/components/logic/atoms/input/props";
import * as useInputWrapperAtom from "@/lib/components/logic/atoms/input/useInputWrapperAtom";
import {
  emitsDefinition,
  pickProps,
  propsDefinition,
} from "@/lib/composables/componentComposable";
import { useTemplateRef } from "@/lib/composables/useTemplateRef";

type UseTextareaAtomScoped = UseInputWrapperAtomScoped & {
  ariaLabelledby?: string;
  autoResize?: boolean | { max?: number; min?: number };
  maxlength?: number;
  minHeight?: string;
  minlength?: number;
  modelValue?: string | null;
  name: string;
  placeholder?: string | null;
  required?: boolean;
  size?: Size;
};

type UseTextareaAtomProps = UseInputWrapperAtomProps &
  UseTextareaAtomScoped & {
    ariaDescribedby?: string;
    ariaErrormessage?: string;
    id?: string;
    invalid?: boolean;
  };

type UseTextareaAtomEmit = (
  event: "blur" | "focus" | "update:modelValue",
  name: string,
) => void;

const scoped = propsDefinition({
  ...useInputWrapperAtom.scoped,
  ariaLabelledby: { type: String, required: false },
  autoResize: {
    type: [Boolean, Object] as PropType<
      boolean | { max?: number; min?: number }
    >,
    default: false,
  },
  maxlength: { type: Number, required: false },
  minHeight: { type: String, required: false },
  minlength: { type: Number, required: false },
  modelValue: {
    type: String as PropType<string | null | undefined>,
    default: null,
  },
  name: { type: String, required: true },
  placeholder: { type: String as PropType<string | null>, required: false },
  required: { type: Boolean, default: false },
  size,
}) satisfies Record<keyof UseTextareaAtomScoped, unknown>;

const props = propsDefinition({
  ...useInputWrapperAtom.props,
  ...scoped,
  ariaDescribedby: { type: String, required: false },
  ariaErrormessage: { type: String, required: false },
  id: { type: String, required: false },
  invalid: { type: Boolean, default: false },
}) satisfies Record<keyof UseTextareaAtomProps, unknown>;

const emits = emitsDefinition(["blur", "focus", "update:modelValue"]);

function use(props: UseTextareaAtomProps, emit: UseTextareaAtomEmit) {
  const inputWrapperAtom = {
    props: pickProps(props, useInputWrapperAtom.props),
  };

  const { el: textarea, ref: textareaRef } =
    useTemplateRef<HTMLTextAreaElement>();
  const computedUseTextArea = computed(() => {
    return props.autoResize && textarea.value ? textarea.value : undefined;
  });
  useTextareaAutosize({
    input: toRef(() => props.modelValue ?? ""),
    element: computedUseTextArea,
  });

  const height = computed(() => {
    const baseAutoResizeHeightValues = { min: 32, max: 128 };
    if (props.autoResize === true) {
      return baseAutoResizeHeightValues;
    }
    if (!props.autoResize) {
      return { min: baseAutoResizeHeightValues.min, max: 999 };
    }
    const min = props.autoResize.min ?? baseAutoResizeHeightValues.min;
    const max = props.autoResize.max ?? baseAutoResizeHeightValues.max;
    return {
      min: Math.min(min, max),
      max: Math.max(min, max),
    };
  });

  watchEffect(() => {
    if (textarea.value) {
      textarea.value.style.minHeight =
        props.minHeight ?? `${height.value.min * 4}px`;
      textarea.value.style.maxHeight = `${height.value.max * 4}px`;
      textarea.value.style.resize = props.autoResize ? "none" : "vertical";
    }
  });

  function onBlur() {
    emit("blur", props.name);
  }

  function onFocus() {
    emit("focus", props.name);
  }

  const onInput = (event: InputEvent) => {
    const inputValue = (event.target as HTMLInputElement).value;
    emit("update:modelValue", inputValue);
  };

  return {
    inputWrapperAtom,
    input: {
      props: reactive({
        id: toRef(() => props.id),
        name: toRef(() => props.name),
        placeholder: toRef(() => props.placeholder ?? undefined),
        disabled: toRef(() => props.disabled),
        required: toRef(() => props.required),
        "aria-invalid": toRef(() =>
          props.invalid ? ("true" as const) : undefined,
        ),
        maxlength: toRef(() => props.maxlength),
        minlength: toRef(() => props.minlength),
        "aria-errormessage": toRef(() => props.ariaErrormessage),
        "aria-describedby": toRef(() => props.ariaDescribedby),
        "aria-labelledby": toRef(() => props.ariaLabelledby),
        value: toRef(() => props.modelValue ?? undefined),
      }),
      on: {
        input: onInput,
        blur: onBlur,
        focus: onFocus,
      },
      ref: textareaRef,
    },
    size: toRef(() => props.size),
    height,
  };
}

export type {
  UseTextareaAtomEmit,
  UseTextareaAtomProps,
  UseTextareaAtomScoped,
};
export { emits, props, scoped, use };
